import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  administrationApiService, 
  Administration, 
  AdministrationCreateDTO, 
  AdministrationUpdateDTO, 
  AdministrationFilters 
} from '../api/administration-api';
import { CondominiumAdministration } from '../types/administration.types';

// Estado inicial
interface AdministrationState {
  administrations: CondominiumAdministration[];
  currentAdministration: CondominiumAdministration | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: AdministrationFilters;
  stats: {
    total: number;
    meetings: number;
    documents: number;
    procedures: number;
    contracts: number;
    pending: number;
    completed: number;
  } | null;
}

const initialState: AdministrationState = {
  administrations: [],
  currentAdministration: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {},
  stats: null,
};

// Función para convertir Administration de API a CondominiumAdministration
const mapAdministrationToCondominium = (administration: Administration): CondominiumAdministration => ({
  id: administration.id,
  title: administration.title,
  type: administration.type,
  status: administration.status,
  description: administration.description,
  date: administration.date,
  createdAt: administration.createdAt,
  updatedAt: administration.updatedAt,
});

// Async Thunks
export const fetchAdministrations = createAsyncThunk(
  'administration/fetchAdministrations',
  async (filters: AdministrationFilters = {}, { rejectWithValue }) => {
    try {
      const response = await administrationApiService.getAdministrations(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar registros administrativos'
      );
    }
  }
);

export const fetchAdministration = createAsyncThunk(
  'administration/fetchAdministration',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await administrationApiService.getAdministrationById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar registro administrativo'
      );
    }
  }
);

export const createAdministration = createAsyncThunk(
  'administration/createAdministration',
  async (data: AdministrationCreateDTO, { rejectWithValue }) => {
    try {
      const response = await administrationApiService.createAdministration(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al crear registro administrativo'
      );
    }
  }
);

export const updateAdministration = createAsyncThunk(
  'administration/updateAdministration',
  async (
    { id, data }: { id: string; data: AdministrationUpdateDTO },
    { rejectWithValue }
  ) => {
    try {
      const response = await administrationApiService.updateAdministration(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al actualizar registro administrativo'
      );
    }
  }
);

export const deleteAdministration = createAsyncThunk(
  'administration/deleteAdministration',
  async (id: string, { rejectWithValue }) => {
    try {
      await administrationApiService.deleteAdministration(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al eliminar registro administrativo'
      );
    }
  }
);

export const toggleAdministrationStatus = createAsyncThunk(
  'administration/toggleAdministrationStatus',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await administrationApiService.toggleAdministrationStatus(id);
      return mapAdministrationToCondominium(response);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cambiar estado del registro administrativo'
      );
    }
  }
);

export const fetchAdministrationStats = createAsyncThunk(
  'administration/fetchAdministrationStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await administrationApiService.getAdministrationStats();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar estadísticas administrativas'
      );
    }
  }
);

// Slice
const administrationSlice = createSlice({
  name: 'administration',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<AdministrationFilters>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setPagination: (state, action: PayloadAction<{ page: number; limit: number }>) => {
      state.pagination.page = action.payload.page;
      state.pagination.limit = action.payload.limit;
    },
    clearError: (state) => {
      state.error = null;
    },
    setCurrentAdministration: (state, action: PayloadAction<CondominiumAdministration | null>) => {
      state.currentAdministration = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch administrations
      .addCase(fetchAdministrations.pending, (state) => {
        if (state.administrations.length === 0) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchAdministrations.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.administrations = (payload.administrations || []).map(mapAdministrationToCondominium);
        
        const newPage = payload.page || 1;
        const newLimit = payload.limit || 10;
        const newTotal = payload.total || 0;
        const newTotalPages = payload.totalPages || 1;
        
        if (
          state.pagination.page !== newPage ||
          state.pagination.limit !== newLimit ||
          state.pagination.total !== newTotal ||
          state.pagination.totalPages !== newTotalPages
        ) {
          state.pagination = {
            page: newPage,
            limit: newLimit,
            total: newTotal,
            totalPages: newTotalPages,
          };
        }
      })
      .addCase(fetchAdministrations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Fetch single administration
      .addCase(fetchAdministration.fulfilled, (state, action) => {
        state.currentAdministration = mapAdministrationToCondominium(action.payload);
      })
      
      // Create administration
      .addCase(createAdministration.fulfilled, (state, action) => {
        state.administrations.unshift(mapAdministrationToCondominium(action.payload));
        state.pagination.total += 1;
      })
      
      // Update administration
      .addCase(updateAdministration.fulfilled, (state, action) => {
        const updated = mapAdministrationToCondominium(action.payload);
        const index = state.administrations.findIndex((a) => a.id === updated.id);
        if (index !== -1) {
          state.administrations[index] = updated;
        }
        if (state.currentAdministration?.id === updated.id) {
          state.currentAdministration = updated;
        }
      })
      
      // Delete administration
      .addCase(deleteAdministration.fulfilled, (state, action) => {
        state.administrations = state.administrations.filter((a) => a.id !== action.payload);
        state.pagination.total -= 1;
      })
      
      // Toggle status
      .addCase(toggleAdministrationStatus.pending, (state) => {
        state.error = null;
      })
      .addCase(toggleAdministrationStatus.fulfilled, (state, action) => {
        const updatedAdministration = action.payload;
        const index = state.administrations.findIndex(
          (admin) => admin.id === updatedAdministration.id
        );
        if (index !== -1) {
          state.administrations[index] = updatedAdministration;
        }
        if (state.currentAdministration?.id === updatedAdministration.id) {
          state.currentAdministration = updatedAdministration;
        }
      })
      .addCase(toggleAdministrationStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Fetch stats
      .addCase(fetchAdministrationStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      });
  },
});

// Actions
export const { setFilters, clearFilters, setPagination, clearError, setCurrentAdministration } =
  administrationSlice.actions;

// Selectors
export const selectAdministrations = (state: { administration: AdministrationState }) =>
  state.administration.administrations;
export const selectCurrentAdministration = (state: { administration: AdministrationState }) =>
  state.administration.currentAdministration;
export const selectAdministrationsLoading = (state: { administration: AdministrationState }) =>
  state.administration.loading;
export const selectAdministrationsError = (state: { administration: AdministrationState }) =>
  state.administration.error;
export const selectAdministrationsPagination = (state: { administration: AdministrationState }) =>
  state.administration.pagination;
export const selectAdministrationsFilters = (state: { administration: AdministrationState }) =>
  state.administration.filters;
export const selectAdministrationStats = (state: { administration: AdministrationState }) =>
  state.administration.stats;

export default administrationSlice.reducer;












